import { BaseModel, SharableModel } from "./base";
import { ResourceStatus } from "./status";
import { GroupableResourceType } from "./resource-types";

// ============================================================================
// Resource Type
// ============================================================================

/**
 * Types of resources that can be organized in groups.
 * Re-exported from centralized resource-types for backward compatibility.
 */
export type ResourceType = GroupableResourceType;

// ============================================================================
// Panel Layout Types (moved from dashboard-group.ts)
// ============================================================================

/**
 * Supported layout types for multi-panel split view.
 *
 * Layouts:
 * - single: 1 dashboard fullscreen
 * - 2-horizontal: 2 dashboards side by side [1][2]
 * - 2-vertical: 2 dashboards stacked [1]/[2]
 * - 3-left: 1 large on left, 2 stacked on right [1][2]/[1][3]
 * - 3-right: 2 stacked on left, 1 large on right [1][2]/[3][2]
 * - 3-top: 1 large on top, 2 side by side on bottom [1]/[2][3]
 * - 3-bottom: 2 side by side on top, 1 large on bottom [1][2]/[3]
 * - 4-grid: 2x2 grid [1][2]/[3][4]
 */
export type PanelLayout =
  | "single"
  | "2-horizontal"
  | "2-vertical"
  | "3-left"
  | "3-right"
  | "3-top"
  | "3-bottom"
  | "4-grid";

// ============================================================================
// Layout Sizes
// ============================================================================

/**
 * Grid sizes for multi-panel layouts.
 * - rowSizes: percentage heights for each row (must sum to 100)
 * - colSizes: percentage widths for each column (must sum to 100)
 */
export interface LayoutSizes {
  rowSizes: number[];
  colSizes: number[];
}

// ============================================================================
// Resource Group Reference (for parent/group field)
// ============================================================================

/**
 * Reference to a resource group.
 * Can be either just an ID or a full group object.
 */
export interface ResourceGroupRef {
  id?: string;
  name?: string | null;
}

/**
 * Helper to get the ID from a ResourceGroupRef.
 * Also handles the case where the ref is already a string ID (for backward compatibility).
 */
export function getGroupRefId(ref: ResourceGroupRef | string | null | undefined): string | undefined {
  if (!ref) return undefined;
  if (typeof ref === "string") return ref;
  return ref.id;
}

// ============================================================================
// Resource Group Model
// ============================================================================

/**
 * Resource group document - organizes resources with shared metadata.
 *
 * Supports nested hierarchy via parent reference. Each group can have a parent group,
 * allowing for arbitrary depth organization (e.g., "Company/Team/Project").
 *
 * The metadata dictionary contains inheritable properties that child resources can use.
 * Each resource type implements its own logic for how to use/merge these metadata.
 */
export interface ResourceGroup extends BaseModel, SharableModel {
  name?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;

  /** Type of resources this group contains */
  resource_type: ResourceType;

  /** Parent group for nested hierarchy (self-reference) */
  parent?: ResourceGroupRef | null;

  /** Inheritable metadata dictionary (e.g., tags, filter, priority) */
  metadata: Record<string, unknown>;

  /** Whether this group is collapsed in the sidebar */
  collapsed?: boolean;

  /** Order in sidebar/parent group */
  order?: number;

  /** Status (active, inactive, draft) */
  status?: ResourceStatus;
}

/**
 * Data required to create a new resource group.
 */
export interface ResourceGroupCreate {
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
  resource_type: ResourceType;
  parent?: string; // Parent group ID
  metadata?: Record<string, unknown>;
  collapsed?: boolean;
  order?: number;
  status?: ResourceStatus;
}

/**
 * Data for updating an existing resource group.
 */
export interface ResourceGroupUpdate {
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
  parent?: string | null;
  metadata?: Record<string, unknown>;
  collapsed?: boolean;
  order?: number;
  status?: ResourceStatus;
}

